
"use client";

import Image from "next/image";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { KeyboardEvent } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper/types";
import { A11y, Keyboard } from "swiper/modules";
import "swiper/css";
import { getStrapiMedia } from "@/app/utils/api-helpers";
import { DestinationsGalleryProps } from "@/app/components/DestinationsGallery";

const ArrowIcon = ({ direction }: { direction: "next" | "prev" }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={direction === "prev" ? "rotate-180" : ""}
  >
    <path
      d="M6 3L10 8L6 13"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type GalleryItem = DestinationsGalleryProps["data"][number];

const getImageSrc = (item?: GalleryItem) =>
  getStrapiMedia(item?.img?.data?.[0]?.attributes?.url ?? "") ?? "";

const chunkSlides = (items: GalleryItem[]) => {
  const chunks: GalleryItem[][] = [];
  for (let index = 0; index < items.length; index += 2) {
    chunks.push(items.slice(index, index + 2));
  }
  return chunks;
};

export const GallerySliderMobile = ({ data }: DestinationsGalleryProps) => {
  const slides = useMemo(() => data ?? [], [data]);
  const groupedSlides = useMemo(() => chunkSlides(slides), [slides]);
  const groupCount = groupedSlides.length;
  const hasLoop = groupCount > 1;
  const [swiperInstance, setSwiperInstance] = useState<SwiperInstance | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
    if (!swiperInstance) {
      return;
    }
    if (hasLoop && typeof swiperInstance.slideToLoop === "function") {
      swiperInstance.slideToLoop(0, 0, false);
    } else {
      swiperInstance.slideTo(0, 0, false);
    }
  }, [groupCount, hasLoop, swiperInstance]);

  const goToSlide = useCallback(
    (direction: "next" | "prev") => {
      if (!swiperInstance) {
        return;
      }
      if (direction === "next") {
        swiperInstance.slideNext();
      } else {
        swiperInstance.slidePrev();
      }
    },
    [swiperInstance]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goToSlide("next");
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToSlide("prev");
      }
    },
    [goToSlide]
  );

  const handleDotClick = useCallback(
    (index: number) => {
      if (!swiperInstance) {
        return;
      }
      if (hasLoop && typeof swiperInstance.slideToLoop === "function") {
        swiperInstance.slideToLoop(index);
      } else {
        swiperInstance.slideTo(index);
      }
    },
    [hasLoop, swiperInstance]
  );

  const handleSwiperInit = useCallback((instance: SwiperInstance) => {
    setSwiperInstance(instance);
    setActiveIndex(instance.realIndex ?? instance.activeIndex ?? 0);
  }, []);

  if (!groupCount) {
    return null;
  }

  return (
    <div
      className="relative w-full overflow-hidden pb-[48px]"
      style={{ touchAction: "pan-y" }}
      role="region"
      aria-roledescription="carousel"
      aria-label="Gallery slider"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <Swiper
        modules={[A11y, Keyboard]}
        slidesPerView={1}
        spaceBetween={16}
        loop={hasLoop}
        allowTouchMove={hasLoop}
        speed={450}
        keyboard={{ enabled: true }}
        onSwiper={handleSwiperInit}
        onSlideChange={(instance) =>
          setActiveIndex(instance.realIndex ?? instance.activeIndex ?? 0)
        }
        className="w-full"
      >
        {groupedSlides.map((group, index) => {
          const isPair = group.length === 2;
          const firstItem = group[0];
          const firstImg = getImageSrc(firstItem);
          const secondImg = isPair ? getImageSrc(group[1]) : "";

          return (
            <SwiperSlide
              key={`${firstItem?.id ?? index}-${index}`}
              className="!flex !w-full justify-center"
            >
              <div className="relative flex w-full flex-shrink-0 justify-center">
                {isPair ? (
                  <div className="flex flex-col items-center gap-1">
                    <Image
                      src={firstImg}
                      alt={firstItem?.text ?? "Preview photo"}
                      width={341}
                      height={284}
                      quality={100}
                      className="h-[284px] w-[341px] rounded-[8px] object-cover"
                      draggable={false}
                      sizes="(max-width: 1024px) 341px, 341px"
                    />
                    <Image
                      src={secondImg}
                      alt={group[1]?.text ?? "Preview photo"}
                      width={341}
                      height={284}
                      quality={100}
                      className="h-[284px] w-[341px] rounded-[8px] object-cover"
                      draggable={false}
                      sizes="(max-width: 1024px) 341px, 341px"
                    />
                  </div>
                ) : (
                  <div className="flex items-start justify-center">
                    <Image
                      src={firstImg}
                      alt={firstItem?.text ?? "Preview photo"}
                      width={341}
                      height={588}
                      quality={100}
                      className="h-[588px] w-[341px] rounded-[8px] object-cover"
                      draggable={false}
                      sizes="(max-width: 1024px) 341px, 341px"
                    />
                  </div>
                )}

                {firstItem?.text && (
                  <span className="absolute bottom-0 left-0 right-0 px-4 py-[16px] text-center text-[20px] font-bold uppercase tracking-[0.04em] text-white">
                    {firstItem.text}
                  </span>
                )}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {hasLoop && (
        <>
          <button
            type="button"
            aria-label="Previous slide"
            className="absolute left-[10px] top-1/2 z-10 flex h-[34px] w-[34px] -translate-y-1/2 items-center justify-center rounded-full border border-[#D7D7D7] bg-white text-black shadow-sm transition hover:border-red hover:text-red focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red"
            onClick={() => goToSlide("prev")}
            disabled={!hasLoop}
          >
            <ArrowIcon direction="prev" />
          </button>
          <button
            type="button"
            aria-label="Next slide"
            className="absolute right-[10px] top-1/2 z-10 flex h-[34px] w-[34px] -translate-y-1/2 items-center justify-center rounded-full border border-[#D7D7D7] bg-white text-black shadow-sm transition hover:border-red hover:text-red focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red"
            onClick={() => goToSlide("next")}
            disabled={!hasLoop}
          >
            <ArrowIcon direction="next" />
          </button>

          <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
            {groupedSlides.map((_, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={`gallery-dot-${index}`}
                  type="button"
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={isActive ? "true" : undefined}
                  className={`h-[6px] w-[6px] rounded-full transition ${
                    isActive ? "bg-red scale-110" : "bg-gray-300 opacity-70"
                  }`}
                  onClick={() => handleDotClick(index)}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
